import { components } from "@appretio/api";

/** Producer prefix of an algorithmic-forecast row: `algo:<model>`. */
const ALGORITHMIC_PRODUCER_PREFIX = "algo:";

/** True for an `algo:<model>` producer value. */
export function isAlgorithmicProducer(value: unknown): value is string {
  return (
    typeof value === "string" && value.startsWith(ALGORITHMIC_PRODUCER_PREFIX)
  );
}

/** The model slug of an `algo:<model>` producer. */
export function modelOfProducer(producer: string): string {
  return producer.slice(ALGORITHMIC_PRODUCER_PREFIX.length);
}

/**
 * The backend's `AlgorithmicModel` wire type also carries `chronos2` and
 * `prophet` — reserved slots with no `MODEL_REGISTRY` entry yet
 * (`models.py`), which answer `model_unavailable` per KPI. Offering only
 * the implemented subset keeps that out of a planner's result.
 * Typed on the wire enum so a typo'd value fails the build.
 */
export const MODEL_OPTIONS: {
  label: string;
  /** Fits the grid's pinned Producer column next to the confidence badge. */
  shortLabel: string;
  value: components["schemas"]["AlgorithmicModel"];
}[] = [
  {
    label: "Seasonal naïve",
    shortLabel: "Seasonal naïve",
    value: "seasonal_naive"
  },
  { label: "ETS (Holt-Winters)", shortLabel: "ETS", value: "ets" },
  {
    label: "ARIMA (STL-deseasonalized)",
    shortLabel: "STL-ARIMA",
    value: "stl_arima"
  },
  { label: "Theta", shortLabel: "Theta", value: "theta" }
];

/** Narrows a MultiSelect's string values back to the wire model union without a cast. */
export function isAlgorithmicModel(
  value: string
): value is components["schemas"]["AlgorithmicModel"] {
  return MODEL_OPTIONS.some((option) => option.value === value);
}

/** The dialog's model label for a wire value, e.g. `"stl_arima"` → `"ARIMA (STL-deseasonalized)"`. Falls back to the raw value for a reserved slot with no menu entry. */
export function getModelLabel(model: string): string {
  return MODEL_OPTIONS.find((option) => option.value === model)?.label ?? model;
}

/** The grid's short model label for a wire value, e.g. `"stl_arima"` → `"STL-ARIMA"`. Same fallback as {@link getModelLabel}. */
export function getModelShortLabel(model: string): string {
  return (
    MODEL_OPTIONS.find((option) => option.value === model)?.shortLabel ?? model
  );
}
